/**
 * Shul hooks. Shuls are stored in /listings (category 'shul') since Phase 1;
 * these hooks translate to and from the old `Shul` shape (src/utils/listingConvert.ts)
 * so every shul component, resolveSchedule() and Next Minyan keep working unchanged.
 */
import { useState, useEffect } from 'react';
import { auth, db } from '../config/firebase';
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
} from 'firebase/firestore';
import { Shul, ShulListing } from '../types';
import {
  listingToShul,
  shulPatchToListingPatch,
  ListingPatchContext,
} from '../utils/listingConvert';
import { LISTINGS_COLLECTION, isPermissionDenied } from './useListings';

/** Public site visibility: approved and not manually deactivated. */
export function isShulPubliclyVisible(
  shul: Pick<Shul, 'active' | 'pendingApproval'>
): boolean {
  return shul.active === true && shul.pendingApproval !== true;
}

/** Same order as the old `orderBy('nameEn')` query. */
function byNameEn(a: Shul, b: Shul): number {
  if (a.nameEn < b.nameEn) return -1;
  if (a.nameEn > b.nameEn) return 1;
  return 0;
}

function shulsFromDocs(docs: { id: string; data: () => unknown }[]): Shul[] {
  return docs
    .map((d) => ({ id: d.id, ...(d.data() as object) } as ShulListing))
    .filter((l) => l.category === 'shul')
    .map(listingToShul)
    .sort(byNameEn);
}

/** Who saved and when; added to every listing write. */
function auditFields(): Record<string, string> {
  const uid = auth.currentUser?.uid;
  return { updatedAt: new Date().toISOString(), ...(uid ? { updatedBy: uid } : {}) };
}

/**
 * Saves a partial old-shape update to /listings/{shulId}.
 * Reads the stored listing first when the change needs it (phone → contacts, active → status).
 */
async function writeShulPatch(
  shulId: string,
  data: Partial<Shul>,
  context?: ListingPatchContext
): Promise<void> {
  let ctx = context;
  if (!ctx && (data.phone !== undefined || data.active !== undefined)) {
    const snap = await getDoc(doc(db, LISTINGS_COLLECTION, shulId));
    const stored = snap.data() as Partial<ShulListing> | undefined;
    ctx = { contacts: stored?.contacts ?? [], status: stored?.status };
  }
  const patch = shulPatchToListingPatch(data, ctx);
  await updateDoc(doc(db, LISTINGS_COLLECTION, shulId), { ...patch, ...auditFields() });
}

export function useShuls() {
  const [shuls, setShuls] = useState<Shul[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(
      collection(db, LISTINGS_COLLECTION),
      where('status', '==', 'published'),
      where('category', '==', 'shul')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        setShuls(shulsFromDocs(snapshot.docs).filter(isShulPubliclyVisible));
        setLoading(false);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  return { shuls, loading, error };
}

/** Listing fields the old `Shul` shape doesn't carry (for the preview banner, plan 16). */
export interface ShulListingStatus {
  status: ShulListing['status'];
  rejectionReason?: string;
}

export function useShul(shulId: string) {
  const [shul, setShul] = useState<Shul | null>(null);
  const [listingStatus, setListingStatus] = useState<ShulListingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!shulId) {
      setShul(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsubscribe = onSnapshot(
      doc(db, LISTINGS_COLLECTION, shulId),
      (snapshot) => {
        const data = snapshot.data() as Omit<ShulListing, 'id'> | undefined;
        if (snapshot.exists() && data?.category === 'shul') {
          setShul(listingToShul({ ...data, id: snapshot.id }));
          setListingStatus({ status: data.status, rejectionReason: data.rejectionReason });
        } else {
          setShul(null);
          setListingStatus(null);
        }
        setLoading(false);
      },
      (err) => {
        // A hidden or pending shul the visitor may not read: same as "not found",
        // which is what the old /shuls collection showed (ShulPage hid it).
        setListingStatus(null);
        if (isPermissionDenied(err)) {
          setShul(null);
        } else {
          setError(err.message);
        }
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [shulId]);

  return { shul, listingStatus, loading, error };
}

export function useUpdateShul(shulId: string) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateShul = async (data: Partial<Shul>) => {
    if (!shulId) return;
    setLoading(true);
    setError(null);
    try {
      await writeShulPatch(shulId, data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return { updateShul, loading, error };
}
